import React from "react";
import { tokens } from "../../tokens";
import { css } from "../../lib/panda";

export interface LinkProps
  extends Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, "color"> {
  variant?: "default" | "subtle" | "contrast";
  underline?: "hover" | "always" | "none";
  size?: "sm" | "md" | "lg";
  external?: boolean;
}

export const Link = React.forwardRef<HTMLAnchorElement, LinkProps>(
  (
    {
      children,
      href,
      variant = "default",
      underline = "hover",
      size = "md",
      external,
      style,
      rel,
      target,
      ...props
    },
    ref
  ) => {
    const colorByVariant: Record<NonNullable<LinkProps["variant"]>, string> = {
      default: tokens.colors.brand.primary,
      subtle: tokens.colors.neutral[600],
      contrast: tokens.colors.neutral[900],
    };

    const fontSizeBySize: Record<NonNullable<LinkProps["size"]>, string> = {
      sm: "0.875rem",
      md: "1rem",
      lg: "1.125rem",
    };

    const computedRel =
      external || target === "_blank"
        ? [rel, "noopener", "noreferrer"].filter(Boolean).join(" ")
        : rel;

    const computedTarget = external ? "_blank" : target;

    const baseStyles = css({
      color: colorByVariant[variant],
      fontSize: fontSizeBySize[size],
      fontWeight: 600,
      textDecoration: underline === "always" ? "underline" : "none",
      cursor: "pointer",
      transition: "color 0.15s ease, text-decoration-color 0.15s ease",
      ...(underline === "hover"
        ? { borderBottom: `2px solid transparent` }
        : {}),
      "&:hover": {
        color:
          variant === "subtle"
            ? tokens.colors.neutral[700]
            : tokens.colors.brand.accent,
        ...(underline === "hover"
          ? {
              textDecoration: "none",
              borderBottom: `2px solid ${tokens.colors.brand.accent}`,
            }
          : {}),
      },
      "&:focus-visible": {
        outline: `2px solid ${tokens.colors.brand.accent}`,
        outlineOffset: "2px",
        borderRadius: "4px",
      },
    });

    return (
      <a
        ref={ref}
        href={href}
        target={computedTarget}
        rel={computedRel}
        style={{ ...baseStyles, ...style }}
        {...props}
      >
        {children}
      </a>
    );
  }
);

Link.displayName = "Link";
